// The room's state, followed rather than polled.
//
// The page used to refetch /inspect whenever the socket's latestSeq changed.
// That leaves a window where we know the room moved but still render what it
// held before — and an edit compiled in that window cites a stale baseRev and
// gets rejected for nothing. Following the ops instead closes it: Replica folds
// each broadcast in roomRev order and tells us exactly when we're behind, which
// a sequence number can't.

import { useCallback, useEffect, useRef, useState } from "react";
import { Replica } from "@shared/sync/replica.js";
import type { OpBroadcast } from "@shared/sync/intents.js";
import type { CanonicalRoom } from "@shared/sync/state.js";
import { roomState } from "@web-shared/lib/api";

export interface RoomReplica {
  /** Null until the first bootstrap lands. */
  room: CanonicalRoom | null;
  error: string | null;
  /** Re-read the whole truth. Also the recovery path after a stale rejection. */
  refresh: () => void;
  /** Fold one broadcast in. Wire this to the socket. */
  ingest: (payload: unknown) => void;
  replica: Replica;
}

function isBroadcast(v: unknown): v is OpBroadcast {
  return typeof v === "object" && v !== null && (v as OpBroadcast).v === 3;
}

/** The replica mutates its room in place, so hand React a fresh object. */
function snapshot(room: CanonicalRoom): CanonicalRoom {
  return { ...room, tracks: [...room.tracks], clips: [...room.clips] };
}

export function useRoomReplica(jamId: string | undefined): RoomReplica {
  const replicaRef = useRef(new Replica());
  const [room, setRoom] = useState<CanonicalRoom | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guards against a bootstrap that resolves after a newer one, which would
  // rewind the replica to an older roomRev and strand it behind the broadcasts.
  const bootstrapId = useRef(0);

  const refresh = useCallback(() => {
    if (!jamId) return;
    const mine = ++bootstrapId.current;
    roomState(jamId)
      .then((state) => {
        if (mine !== bootstrapId.current) return;
        // The response's `files` map of presigned URLs is ignored on purpose:
        // this app streams R2 through its own /api/r2/object route, which is
        // token-gated and never expires out from under a long-open page.
        replicaRef.current.adoptFullState(state);
        setRoom(snapshot(replicaRef.current.room));
        setError(null);
      })
      .catch((e: unknown) => {
        if (mine !== bootstrapId.current) return;
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [jamId]);

  const ingest = useCallback(
    (payload: unknown) => {
      if (!isBroadcast(payload)) return;
      const outcome = replicaRef.current.applyBroadcast(payload);
      if (outcome.needsFullState) {
        // A gap. Applying ops out of order lands the replica in a state neither
        // side ever held, so re-read rather than guess.
        refresh();
        return;
      }
      if (outcome.duplicate) return;
      setRoom(snapshot(replicaRef.current.room));
    },
    [refresh],
  );

  useEffect(() => {
    replicaRef.current = new Replica();
    setRoom(null);
    setError(null);
    refresh();
  }, [jamId, refresh]);

  return { room, error, refresh, ingest, replica: replicaRef.current };
}
